import axios from "axios";
import apiUrls from "src/api";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IAppLoaderAction } from "src/common/state/loaderHandleMiddleware";
import { UserFormResults } from "../components/chatState";
import {
  IGptConversation,
  PlanPhase,
  ScenariaPlan,
  ScenariaPlanSceneGenerationMetadata,
} from "src/model/model";
import { RootState } from "src/common/state/store";
import { IMedia } from "src/model/unityObject";
import { getMedia } from "src/features/gallery/state/gallerySlice";
import { syncUserWithServer } from "src/features/profile/state/profileSlice";

const GPT_CONVERSATIONS_PAGE_SIZE = 20;

interface IGptConversationsPagination {
  total: number;
  hasMore: boolean;
  isLoadingMore: boolean;
}

interface IPlanModeProgress {
  status: string | null;
  message: string | null;
  done?: number;
  total?: number;
}

interface IWixarAIStore {
  activeGptConversation?: IGptConversation | null;
  gptConversations: IGptConversation[];
  gptConversationsPagination: IGptConversationsPagination;
  idOfLatestProjectGroupCursedByAi: number | null;
  scenarioHelperContextPdfs: IMedia[];
  scenarioHelperContextImages: IMedia[];
  scenarioHelperRagAgentIds: number[];
  chosenLlmProvider: string;
  lastAiModuleConversationId: number | null;
  lastAiModuleOutputIndex: number | null;
  lastAiModuleGenerateBlockadeImages: boolean;
  planModeProgress: IPlanModeProgress;
  // Unix ms captured when the user kicks off Blockade generation. Used to
  // render an elapsed-time chip + soft ETA in GenerationRecap. Ephemeral
  // (not persisted) — on reload, the banner still shows "generating…" but
  // the timer is absent. Acceptable for a UX nicety.
  generationStartedAt: number | null;
}

const initialPagination: IGptConversationsPagination = {
  total: 0,
  hasMore: true,
  isLoadingMore: false,
};

const initialPlanModeProgress: IPlanModeProgress = {
  status: null,
  message: null,
};

// Only the server transitions out of 'generating' (success → 'generated',
// failure or reconcile sweep → 'ready_to_generate'). If local state still says
// 'generating' but the server has moved on, trust the server — otherwise a
// stale phase rehydrated from localStorage masks the reset forever.
const adoptServerPhaseIfExitingGeneration = (
  localPhase: PlanPhase | undefined,
  serverPhase: PlanPhase | undefined,
): PlanPhase | undefined =>
  localPhase === "generating" && serverPhase && serverPhase !== "generating"
    ? serverPhase
    : localPhase;

const initialState: IWixarAIStore = {
  activeGptConversation: null,
  gptConversations: [],
  gptConversationsPagination: initialPagination,
  idOfLatestProjectGroupCursedByAi: null,
  scenarioHelperContextPdfs: [],
  scenarioHelperContextImages: [],
  scenarioHelperRagAgentIds: [],
  chosenLlmProvider: "openai",
  lastAiModuleConversationId: null,
  lastAiModuleOutputIndex: null,
  lastAiModuleGenerateBlockadeImages: false,
  planModeProgress: { ...initialPlanModeProgress },
  generationStartedAt: null,
};

export const fetchGptConversations = createAsyncThunk(
  "gpt/fetchGptConversations",
  async ({
    start = 0,
    limit = GPT_CONVERSATIONS_PAGE_SIZE,
  }: { start?: number; limit?: number } & IAppLoaderAction) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    const response = await fetch(
      `${apiUrls.gpt.findAllWithNestedStuff}?start=${start}&limit=${limit}`,
      { headers: { Authorization: `Bearer ${jwt}` } },
    );
    const body = await response.json();
    return { data: body.data, total: body.total ?? 0 };
  },
);

// Look up the originating gpt-conversation for a given project so the
// "Voir la conversation d'origine" CTA on the project Information panel
// can pre-load it into the chatbot view via setActiveGptConversation.
// Resolves to `null` on 404 (legacy project without a linked conversation)
// so callers can disable the button gracefully instead of error-handling.
export const fetchGptConversationForProject = createAsyncThunk(
  "gpt/fetchGptConversationForProject",
  async ({ projectId }: { projectId: number } & IAppLoaderAction) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    const response = await fetch(`${apiUrls.gpt.conversationForProject}/${projectId}`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(`fetchGptConversationForProject failed: ${response.status}`);
    }
    const body = await response.json();
    return body.data ?? null;
  },
);

export const fetchMoreGptConversations = createAsyncThunk(
  "gpt/fetchMoreGptConversations",
  async ({ start, limit = GPT_CONVERSATIONS_PAGE_SIZE }: { start: number; limit?: number }) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    const response = await fetch(
      `${apiUrls.gpt.findAllWithNestedStuff}?start=${start}&limit=${limit}`,
      { headers: { Authorization: `Bearer ${jwt}` } },
    );
    const body = await response.json();
    return { data: body.data, total: body.total ?? 0 };
  },
);

export const createChat = createAsyncThunk(
  "gpt/createChat",
  async (
    {
      userPrompt,
      userFormResults,
      sourceMediasWithContext = undefined,
      sourceMedias,
      chosenLlmProvider,
      ragAgentIds = [],
      excludeContextLayers = [],
      cb,
      cbOnRequestEnd,
    }: {
      userPrompt: string;
      userFormResults: UserFormResults;
      sourceMediasWithContext?: number[];
      sourceMedias;
      chosenLlmProvider: string;
      ragAgentIds?: number[];
      excludeContextLayers?: string[];
      cb?: (chunk: string) => void;
      cbOnRequestEnd?: () => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.createChat, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          data: {
            userPrompt,
            userFormResults,
            sourceMediasWithContext,
            service: chosenLlmProvider,
            rag_agent_ids: ragAgentIds,
            excludeContextLayers,
          },
        }),
      });

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let botMessage = "";
      let metaReceived = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const chunk = decoder.decode(value);
          botMessage += chunk;

          // \x1E (Record Separator) delimits GPT text from backend metadata.
          // Don't forward the metadata portion to the streaming display callback.
          if (!metaReceived) {
            const sepIdx = chunk.indexOf("\x1E");
            if (sepIdx !== -1) {
              metaReceived = true;
              if (cb && sepIdx > 0) cb(chunk.slice(0, sepIdx));
            } else if (cb) {
              cb(chunk);
            }
          }
        }
      }

      // Extract conversation ID from backend metadata appended after \x1E
      let conversationId: number | undefined;
      const sepIdx = botMessage.indexOf("\x1E");
      if (sepIdx !== -1) {
        try {
          const meta = JSON.parse(botMessage.slice(sepIdx + 1));
          conversationId = meta.conversationId;
        } catch {
          /* metadata parse failed — continue without ID */
        }
        botMessage = botMessage.slice(0, sepIdx);
      }

      const timestamp = new Date().toISOString();

      const newConversation: IGptConversation = {
        id: conversationId,
        conversation_metadata: {
          model: "gpt-4",
          timestamp,
          userPrompts: [userPrompt],
          userFormResults: userFormResults,
          source_media_with_context: [],
        },
        conversation_data: {
          output: [botMessage],
        },
        createdAt: timestamp,
        updatedAt: timestamp,
        source_media_with_context: sourceMedias.filter((sm) =>
          sourceMediasWithContext.includes(Number(sm.id)),
        ),
      };

      if (cbOnRequestEnd) {
        cbOnRequestEnd();
      }

      return newConversation;
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (createChat)");
    }
  },
);

export const updateChat = createAsyncThunk(
  "gpt/updateChat",
  async (
    {
      userPrompt,
      conversationId,
      ragAgentIds = [],
      cb,
    }: {
      userPrompt: string;
      conversationId: number;
      ragAgentIds?: number[];
      cb?: (chunk: string) => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.updateChat, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ userPrompt, conversationId, rag_agent_ids: ragAgentIds }),
      });

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let botMessage = "";

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const chunk = decoder.decode(value);
          botMessage += chunk;

          if (cb) {
            cb(chunk);
          }
        }
      }

      return { userPrompt, botResponse: botMessage };
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (updateChat)");
    }
  },
);

export const resetChat = createAsyncThunk(
  "gpt/resetChat",
  async ({ cb }: { cb(): any } & IAppLoaderAction) => {
    return await axios
      .put(apiUrls.gpt.resetChat)
      .then((res) => {
        cb();
        return res;
      })
      .catch((e) => {
        console.error("resetChat did an oopsie: ", e);
        return e;
      });
  },
);

export const askWixarpedia = createAsyncThunk(
  "gpt/askWixarpedia",
  async (
    {
      userPrompt,
      cb,
    }: {
      userPrompt: string;
      cb?: (chunk: string) => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.askWixarpedia, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          data: {
            userPrompt,
          },
        }),
      });

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let botMessage = "";

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const chunk = decoder.decode(value);
          botMessage += chunk;

          if (cb) {
            cb(chunk);
          }
        }
      }

      return botMessage;
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (askWixarpedia)");
    }
  },
);

export const updateGptConversationName = createAsyncThunk(
  "gpt/updateName",
  async (
    {
      id,
      newName,
      cb,
    }: {
      id: number;
      newName: string;
      cb?: () => void;
    },
    { rejectWithValue },
  ) => {
    try {
      return await axios
        .put(`${apiUrls.gpt.gptConversations}/${id}`, {
          name: newName,
        })
        .then((res) => {
          if (cb) {
            cb();
          }
          return res.data;
        })
        .catch((e) => {
          console.error("Error in chatbotSlice.ts :", e);
          throw e;
        });
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (updateGptConversationName)");
    }
  },
);

export const deleteGptConversation = createAsyncThunk(
  "gpt/deleteName",
  async (
    {
      id,
      cb,
    }: {
      id: number;
      cb?: () => void;
    },
    { rejectWithValue },
  ) => {
    try {
      return await axios
        .delete(`${apiUrls.gpt.gptConversations}/${id}`)
        .then((res) => {
          if (cb) {
            cb();
          }
          return res.data;
        })
        .catch((e) => {
          console.error("Error in chatbotSlice.ts 299:", e);
          throw e;
        });
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (deleteGptConversation)");
    }
  },
);

export const generateProjectGroupFromAiGraph = createAsyncThunk(
  "gpt/generateProjectGroupFromAiGraph",
  async (
    {
      conversationId,
      name,
      generateBlockadeImages = false,
      relatedGptOutputIndex = undefined,
      cb,
    }: {
      conversationId: number;
      name: string;
      generateBlockadeImages?: boolean;
      relatedGptOutputIndex?: number;
      cb?: () => void;
    } & IAppLoaderAction,
    { rejectWithValue },
  ) => {
    try {
      const jwt = localStorage.getItem("wixar.auth.jwt");
      if (!jwt) {
        throw new Error("JWT token not found in localStorage.");
      }

      const response = await fetch(`${apiUrls.gpt.generateTemplate}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          name,
          conversationId,
          generateBlockadeImages,
          relatedGptOutputIndex,
          createAsProjectGroup: true,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch the API");
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Readable stream is not supported");
      }

      const decoder = new TextDecoder();
      let result = "";

      const readNextChunk = async () => {
        const { done, value } = await reader.read();

        if (done) return; // End recursion when the stream is done

        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          chunk
            .trim()
            .split("\n")
            .forEach((line) => {
              if (!line) return;

              try {
                const parsed = JSON.parse(line);
                if (parsed.status === "complete") {
                  result = parsed.data;
                } else if (parsed.error) {
                  throw new Error(parsed.error);
                }
              } catch (error) {
                console.error("Error parsing chunk:", error, "Raw chunk:", line);
              }
            });
        }

        // Recursively read the next chunk
        await readNextChunk();
      };

      await readNextChunk(); // Start the recursive read

      if (cb) cb();

      return result; // Return final result
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (generateProjectGroupFromAiGraph)");
    }
  },
);

export const fetchAndUpdateSpecificConversation = createAsyncThunk(
  "gpt/fetchAndUpdateSpecificConversation",
  async ({ conversationId }: { conversationId: number }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrls.gpt.gptConversations}/${conversationId}`);

      if (!response.data) {
        throw new Error("No conversation data returned");
      }

      return response.data;
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (fetchAndUpdateSpecificConversation)");
    }
  },
);

// ─── Plan-mode thunks ─────────────────────────────────────────────────────

export const startPlanModeChat = createAsyncThunk(
  "gpt/startPlanModeChat",
  async (
    {
      userPrompt,
      userFormResults,
      sourceMediasWithContext = [],
      sourceMedias,
      chosenLlmProvider,
      ragAgentIds = [],
      excludeContextLayers = [],
      cb,
      cbOnRequestEnd,
    }: {
      userPrompt: string;
      userFormResults: UserFormResults;
      sourceMediasWithContext?: number[];
      sourceMedias: IMedia[];
      chosenLlmProvider: string;
      ragAgentIds?: number[];
      excludeContextLayers?: string[];
      cb?: (chunk: string) => void;
      cbOnRequestEnd?: () => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.planMode.start, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          data: {
            userPrompt,
            userFormResults,
            sourceMediasWithContext,
            service: chosenLlmProvider,
            rag_agent_ids: ragAgentIds,
            excludeContextLayers,
          },
        }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `HTTP ${response.status}`);
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let botMessage = "";
      let metaReceived = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const chunk = decoder.decode(value);
          botMessage += chunk;

          if (!metaReceived) {
            const sepIdx = chunk.indexOf("\x1E");
            if (sepIdx !== -1) {
              metaReceived = true;
              if (cb && sepIdx > 0) cb(chunk.slice(0, sepIdx));
            } else if (cb) {
              cb(chunk);
            }
          }
        }
      }

      let conversationId: number | undefined;
      const sepIdx = botMessage.indexOf("\x1E");
      if (sepIdx !== -1) {
        try {
          const meta = JSON.parse(botMessage.slice(sepIdx + 1));
          conversationId = meta.conversationId;
        } catch {
          /* metadata parse failed */
        }
        botMessage = botMessage.slice(0, sepIdx);
      }

      if (!conversationId) {
        throw new Error("Impossible de récupérer l'identifiant de la conversation — réessayez.");
      }

      const timestamp = new Date().toISOString();

      const newConversation: IGptConversation = {
        id: conversationId,
        plan_phase: "intake",
        conversation_metadata: {
          model: chosenLlmProvider === "mistral" ? "mistral" : "gpt-4",
          timestamp,
          userPrompts: [userPrompt],
          userFormResults: userFormResults,
          source_media_with_context: [],
        },
        conversation_data: {
          output: [botMessage],
        },
        createdAt: timestamp,
        updatedAt: timestamp,
        source_media_with_context: sourceMedias.filter((sm) =>
          sourceMediasWithContext.includes(Number(sm.id)),
        ),
      };

      if (cbOnRequestEnd) {
        cbOnRequestEnd();
      }

      return newConversation;
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (startPlanModeChat)");
    }
  },
);

export interface PlanToolCallEvent {
  event: "transition_requested" | "transition_rejected";
  accepted: boolean;
  reason?: string;
  syntheticAck?: string;
  fallbackText?: string;
}

export const continuePlanModeChat = createAsyncThunk(
  "gpt/continuePlanModeChat",
  async (
    {
      userPrompt,
      conversationId,
      ragAgentIds = [],
      sourceMediasWithContext,
      cb,
    }: {
      userPrompt: string;
      conversationId: number;
      ragAgentIds?: number[];
      sourceMediasWithContext?: number[];
      cb?: (chunk: string) => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.planMode.continue, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          userPrompt,
          conversationId,
          rag_agent_ids: ragAgentIds,
          sourceMediasWithContext,
        }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `HTTP ${response.status}`);
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let botMessage = "";
      let toolEvent: PlanToolCallEvent | null = null;
      let errorMessage: string | null = null;
      let streamDone = false;

      const handleEvent = (eventName: string, data: any) => {
        if (eventName === "text") {
          const chunk: string = typeof data?.chunk === "string" ? data.chunk : "";
          if (!chunk) return;
          botMessage += chunk;
          if (cb) cb(chunk);
        } else if (eventName === "transition_requested") {
          // Push the synthetic ack into the bubble immediately — this is
          // what makes the handoff feel instant. The same string is also
          // persisted server-side so a reload produces the same history.
          const ack: string =
            typeof data?.syntheticAck === "string"
              ? data.syntheticAck
              : "OK, je consolide votre plan…";
          if (cb) cb(ack);
          botMessage += ack;
          toolEvent = {
            event: "transition_requested",
            accepted: !!data?.accepted,
            syntheticAck: ack,
          };
        } else if (eventName === "transition_rejected") {
          // Push the fallback text into the bubble so the user sees a
          // conversational answer instead of an empty bubble.
          const fallback: string = typeof data?.fallbackText === "string" ? data.fallbackText : "";
          if (fallback) {
            if (cb) cb(fallback);
            botMessage += fallback;
          }
          toolEvent = {
            event: "transition_rejected",
            accepted: false,
            reason: data?.reason,
            fallbackText: fallback,
          };
        } else if (eventName === "error") {
          errorMessage = data?.message || "stream error";
        }
        // event: "done" → no-op; loop exits when the stream closes anyway.
      };

      while (!streamDone) {
        const { value, done: readerDone } = await reader.read();
        if (readerDone) {
          streamDone = true;
          break;
        }
        if (!value) continue;
        buffer += decoder.decode(value, { stream: true });

        // SSE messages are separated by a blank line.
        let sepIdx;
        while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);

          let eventName = "message";
          const dataLines: string[] = [];
          for (const line of rawEvent.split("\n")) {
            if (line.startsWith("event:")) eventName = line.slice(6).trim();
            else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
          }
          const dataStr = dataLines.join("\n");
          if (!dataStr) continue;

          let data: any = null;
          try {
            data = JSON.parse(dataStr);
          } catch (e) {
            console.warn("[continuePlanModeChat] SSE data parse failed:", e, dataStr);
            continue;
          }

          handleEvent(eventName, data);
        }
      }

      if (errorMessage) throw new Error(errorMessage);

      return { userPrompt, botResponse: botMessage, toolEvent };
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (continuePlanModeChat)");
    }
  },
);

export const transitionToReady = createAsyncThunk(
  "gpt/transitionToReady",
  async (
    { conversationId, ragAgentIds = [] }: { conversationId: number; ragAgentIds?: number[] },
    { dispatch, rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      dispatch(
        setPlanModeProgress({
          status: "starting",
          message: "Préparation du plan…",
        }),
      );

      const response = await fetch(apiUrls.gpt.planMode.transitionToReady, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ conversationId, rag_agent_ids: ragAgentIds }),
      });

      if (!response.ok && response.status !== 200) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `HTTP ${response.status}`);
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalPlan: ScenariaPlan | null = null;
      let intakeBoundary: number | null = null;
      let errorMessage: string | null = null;

      try {
        let streamDone = false;
        while (!streamDone) {
          const { value, done } = await reader.read();
          if (done) {
            streamDone = true;
            break;
          }
          if (!value) continue;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            try {
              const parsed = JSON.parse(trimmed);
              if (parsed.status === "describing_images") {
                const total = parsed.progress?.total ?? 0;
                const done = parsed.progress?.done ?? 0;
                dispatch(
                  setPlanModeProgress({
                    status: "describing_images",
                    message:
                      total > 0 ? `Analyse de l'image… (${done}/${total})` : "Analyse des images…",
                    done,
                    total,
                  }),
                );
              } else if (parsed.status === "consolidating_plan") {
                dispatch(
                  setPlanModeProgress({
                    status: "consolidating_plan",
                    message: "Consolidation du plan…",
                  }),
                );
              } else if (parsed.status === "complete") {
                finalPlan = parsed.plan;
                intakeBoundary =
                  typeof parsed.intake_boundary === "number" ? parsed.intake_boundary : null;
              } else if (parsed.status === "error") {
                errorMessage = parsed.message || "Unknown error";
              }
            } catch (e) {
              console.error("Plan-mode chunk parse failed:", e, "raw:", trimmed);
            }
          }
        }
      } finally {
        reader.cancel().catch(() => undefined);
      }

      if (errorMessage) {
        throw new Error(errorMessage);
      }
      if (!finalPlan) {
        throw new Error("No plan returned");
      }

      return { conversationId, plan: finalPlan, intakeBoundary };
    } catch (error: any) {
      console.error("API call failed:", error);
      return rejectWithValue(error?.message || "API call failed (transitionToReady)");
    } finally {
      dispatch(resetPlanModeProgress());
    }
  },
);

export const refinePlanThunk = createAsyncThunk(
  "gpt/refinePlanThunk",
  async (
    {
      conversationId,
      message,
      ragAgentIds = [],
    }: { conversationId: number; message: string; ragAgentIds?: number[] },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.planMode.refine, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          conversationId,
          message,
          rag_agent_ids: ragAgentIds,
        }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        let parsedMessage = text;
        try {
          parsedMessage = JSON.parse(text)?.message || text;
        } catch {
          /* not JSON */
        }
        throw new Error(parsedMessage || `HTTP ${response.status}`);
      }

      const body = await response.json();
      const plan = body.plan as ScenariaPlan;
      if (!plan) throw new Error("No plan returned");
      const mediaTypeOverrides = (body.mediaTypeOverrides ?? []) as Array<{
        sceneIndex: number;
        sceneId: string;
        sceneTitle: string;
        previousMediaType: "image360" | "video360";
        mediaType: "image360" | "video360";
      }>;
      return { conversationId, message, plan, mediaTypeOverrides };
    } catch (error: any) {
      console.error("API call failed:", error);
      return rejectWithValue(error?.message || "API call failed (refinePlanThunk)");
    }
  },
);

// generateFromPlanThunk: backend is POST + SSE; EventSource only does GET, so
// we use fetch + a minimal SSE parser (per Phase 3 step 2 fallback).
export const generateFromPlanThunk = createAsyncThunk(
  "gpt/generateFromPlanThunk",
  async (
    {
      conversationId,
      name,
      createAsProjectGroup = false,
      assignToProjectGroup = undefined,
      generateBlockadeImages = false,
      ragAgentIds = [],
    }: {
      conversationId: number;
      name: string;
      createAsProjectGroup?: boolean;
      assignToProjectGroup?: number;
      generateBlockadeImages?: boolean;
      ragAgentIds?: number[];
    },
    { dispatch, rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.planMode.generate, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          conversationId,
          name,
          createAsProjectGroup,
          assignToProjectGroup,
          generateBlockadeImages,
          rag_agent_ids: ragAgentIds,
        }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        let parsedMessage = text;
        try {
          parsedMessage = JSON.parse(text)?.message || text;
        } catch {
          /* not JSON */
        }
        throw new Error(parsedMessage || `HTTP ${response.status}`);
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let result: {
        projectId: number | null;
        projectGroupId: number | null;
        planPhase: PlanPhase;
      } | null = null;
      let errorMessage: string | null = null;

      try {
        let streamDone = false;
        while (!streamDone) {
          const { value, done } = await reader.read();
          if (done) {
            streamDone = true;
            break;
          }
          if (!value) continue;
          buffer += decoder.decode(value, { stream: true });

          // SSE messages are separated by a blank line.
          let sepIdx;
          while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
            const rawEvent = buffer.slice(0, sepIdx);
            buffer = buffer.slice(sepIdx + 2);

            let eventName = "message";
            const dataLines: string[] = [];
            for (const line of rawEvent.split("\n")) {
              if (line.startsWith("event:")) eventName = line.slice(6).trim();
              else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
            }
            const dataStr = dataLines.join("\n");
            if (!dataStr) continue;

            let data: any = null;
            try {
              data = JSON.parse(dataStr);
            } catch (e) {
              console.error("SSE data parse failed:", e, "raw:", dataStr);
              continue;
            }

            if (eventName === "status") {
              const step: string = data?.step || "";
              const total = data?.total ?? 0;
              const done = data?.done ?? 0;
              const stepLabels: { [k: string]: string } = {
                rendering_markdown: "Rendu du plan…",
                fetching_metadata: "Récupération des métadonnées…",
                creating_project: "Création du module…",
                generating_images:
                  total > 0
                    ? `Génération de ${total} image${total > 1 ? "s" : ""} 360°…`
                    : "Préparation des images…",
                waiting_for_images:
                  total > 0
                    ? `Images 360° en cours (${done}/${total})…`
                    : "Attente des images 360°…",
                assigning_images: "Attribution des images aux scènes…",
              };
              dispatch(
                setPlanModeProgress({
                  status: step || "generating",
                  message: stepLabels[step] || "Génération en cours…",
                  ...(total > 0 && { done, total }),
                }),
              );
            } else if (
              eventName === "scene_image_submitted" ||
              eventName === "scene_image_complete"
            ) {
              if (data?.sceneId && data?.generationMetadata) {
                dispatch(
                  mergeSceneGenerationMetadata({
                    sceneId: data.sceneId,
                    generationMetadata: data.generationMetadata,
                  }),
                );
              }
            } else if (eventName === "complete") {
              result = {
                projectId: data?.projectId ?? null,
                projectGroupId: data?.projectGroupId ?? null,
                planPhase: (data?.planPhase as PlanPhase) || "generated",
              };
            } else if (eventName === "error") {
              errorMessage = data?.message || "Unknown error";
            }
          }
        }
      } finally {
        reader.cancel().catch(() => undefined);
      }

      if (errorMessage) {
        throw new Error(errorMessage);
      }
      if (!result) {
        throw new Error("Connexion perdue — actualisez pour vérifier l'état du module.");
      }

      return { conversationId, ...result };
    } catch (error: any) {
      console.error("API call failed:", error);
      return rejectWithValue(error?.message || "API call failed (generateFromPlanThunk)");
    } finally {
      dispatch(resetPlanModeProgress());
    }
  },
);

export const chatbotSlice = createSlice({
  name: "chatbot",
  initialState,
  reducers: {
    setActiveGptConversation: (state: IWixarAIStore, action) => {
      state.activeGptConversation = action.payload;
      state.chosenLlmProvider = action.payload?.conversation_metadata?.model.includes("mistral")
        ? "mistral"
        : "openai";
    },
    resetActiveGptConversation: (state: IWixarAIStore) => {
      state.activeGptConversation = null;
    },
    setIdOfLatestProjectGroupCursedByAi: (state: IWixarAIStore, action) => {
      state.idOfLatestProjectGroupCursedByAi = action.payload;
    },
    resetIdOfLatestProjectGroupCursedByAi: (state: IWixarAIStore) => {
      state.idOfLatestProjectGroupCursedByAi = null;
    },
    addScenarioHelperContextPdf: (state: IWixarAIStore, action) => {
      if (state.scenarioHelperContextPdfs.length >= 5) return;
      if (state.scenarioHelperContextPdfs.some((p) => p.id === action.payload?.id)) return;
      state.scenarioHelperContextPdfs.push(action.payload);
    },
    removeScenarioHelperContextPdf: (state: IWixarAIStore, action) => {
      state.scenarioHelperContextPdfs = state.scenarioHelperContextPdfs.filter(
        (pdf) => pdf.id !== action.payload,
      );
    },
    setScenarioHelperContextPdf: (state: IWixarAIStore, action) => {
      state.scenarioHelperContextPdfs = action.payload;
    },
    resetScenarioHelperContextPdfs: (state: IWixarAIStore) => {
      state.scenarioHelperContextPdfs = [];
    },
    addScenarioHelperContextImage: (state: IWixarAIStore, action) => {
      if (state.scenarioHelperContextImages.length >= 1) return;
      if (state.scenarioHelperContextImages.some((p) => p.id === action.payload?.id)) return;
      state.scenarioHelperContextImages.push(action.payload);
    },
    removeScenarioHelperContextImage: (state: IWixarAIStore, action) => {
      state.scenarioHelperContextImages = state.scenarioHelperContextImages.filter(
        (img) => img.id !== action.payload,
      );
    },
    setScenarioHelperContextImages: (state: IWixarAIStore, action) => {
      state.scenarioHelperContextImages = action.payload;
    },
    resetScenarioHelperContextImages: (state: IWixarAIStore) => {
      state.scenarioHelperContextImages = [];
    },
    setPlanModeProgress: (state: IWixarAIStore, action) => {
      state.planModeProgress = { ...state.planModeProgress, ...action.payload };
    },
    resetPlanModeProgress: (state: IWixarAIStore) => {
      state.planModeProgress = { ...initialPlanModeProgress };
    },
    // Merge a per-scene generationMetadata patch into the active plan. The
    // SSE channel pushes one of these for each Blockade submission and
    // again on each completion. Silent no-op if the active conversation
    // doesn't match or the sceneId can't be found — protects against late
    // events arriving after the user has navigated away.
    mergeSceneGenerationMetadata: (
      state: IWixarAIStore,
      action: {
        payload: {
          sceneId: string;
          generationMetadata: ScenariaPlanSceneGenerationMetadata;
        };
      },
    ) => {
      const plan = state.activeGptConversation?.conversation_metadata?.plan;
      if (!plan || !Array.isArray(plan.sceneOutline)) return;
      const idx = plan.sceneOutline.findIndex((s) => s.sceneId === action.payload.sceneId);
      if (idx < 0) return;
      plan.sceneOutline[idx] = {
        ...plan.sceneOutline[idx],
        generationMetadata: {
          ...(plan.sceneOutline[idx].generationMetadata || {}),
          ...action.payload.generationMetadata,
        },
      };
    },
    setScenarioHelperRagAgentIds: (state: IWixarAIStore, action) => {
      state.scenarioHelperRagAgentIds = action.payload;
    },
    resetScenarioHelperRagAgentIds: (state: IWixarAIStore) => {
      state.scenarioHelperRagAgentIds = [];
    },
    setChosenLlmProvider: (state: IWixarAIStore, action) => {
      state.chosenLlmProvider = action.payload;
    },
    setLastAiModuleContext: (
      state: IWixarAIStore,
      action: {
        payload: {
          conversationId: number;
          relatedGptOutputIndex: number;
          generateBlockadeImages: boolean;
        };
      },
    ) => {
      state.lastAiModuleConversationId = action.payload.conversationId;
      state.lastAiModuleOutputIndex = action.payload.relatedGptOutputIndex;
      state.lastAiModuleGenerateBlockadeImages = action.payload.generateBlockadeImages;
    },
    resetLastAiModuleContext: (state: IWixarAIStore) => {
      state.lastAiModuleConversationId = null;
      state.lastAiModuleOutputIndex = null;
      state.lastAiModuleGenerateBlockadeImages = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGptConversations.fulfilled, (state, { payload }) => {
      state.gptConversations = payload.data;
      const loaded = Array.isArray(payload.data) ? payload.data.length : 0;
      state.gptConversationsPagination = {
        total: payload.total,
        hasMore: loaded < payload.total,
        isLoadingMore: false,
      };

      const active = state.activeGptConversation;
      if (active?.id) {
        const fromList = (payload.data || []).find(
          (c: IGptConversation) => Number(c.id) === Number(active.id),
        );
        const adopted = adoptServerPhaseIfExitingGeneration(
          active.plan_phase,
          fromList?.plan_phase,
        );
        // Backfill last-generated ids from the list response. On first page
        // load, the active conversation is hydrated from the cached localStorage
        // blob written at login time — which predates these fields. The list
        // endpoint is the first authoritative source we hit, so adopt the ids
        // here so the success card can render "Voir le module" without needing
        // the user to switch conversations first.
        const nextProjectId = active.lastGeneratedProjectId ?? fromList?.lastGeneratedProjectId;
        const nextProjectGroupId =
          active.lastGeneratedProjectGroupId ?? fromList?.lastGeneratedProjectGroupId;
        const phaseChanged = adopted !== active.plan_phase;
        const idsChanged =
          nextProjectId !== active.lastGeneratedProjectId ||
          nextProjectGroupId !== active.lastGeneratedProjectGroupId;
        if (phaseChanged || idsChanged) {
          state.activeGptConversation = {
            ...active,
            plan_phase: adopted,
            lastGeneratedProjectId: nextProjectId,
            lastGeneratedProjectGroupId: nextProjectGroupId,
          };
        }
      }
    });
    builder.addCase(fetchMoreGptConversations.pending, (state) => {
      state.gptConversationsPagination.isLoadingMore = true;
    });
    builder.addCase(fetchMoreGptConversations.fulfilled, (state, { payload }) => {
      const existingIds = new Set(state.gptConversations.map((c) => c.id));
      const newConversations = (payload.data || []).filter(
        (c: IGptConversation) => !existingIds.has(c.id),
      );
      state.gptConversations.push(...newConversations);
      state.gptConversationsPagination = {
        total: payload.total,
        hasMore: state.gptConversations.length < payload.total,
        isLoadingMore: false,
      };
    });
    builder.addCase(fetchMoreGptConversations.rejected, (state) => {
      state.gptConversationsPagination.isLoadingMore = false;
    });
    builder.addCase(createChat.fulfilled, (state, { payload }) => {
      state.activeGptConversation = payload;
      state.gptConversations.push(payload);
      state.gptConversationsPagination.total += 1;
    });
    builder.addCase(updateChat.fulfilled, (state, { payload }) => {
      if (state.activeGptConversation) {
        state.activeGptConversation.conversation_metadata.userPrompts.push(payload.userPrompt);
        state.activeGptConversation.conversation_data.output.push(payload.botResponse);

        // Sync updated conversation back into the list so switching away/back loads fresh data
        const idx = state.gptConversations.findIndex(
          (c) => c.id === state.activeGptConversation?.id,
        );
        if (idx !== -1) {
          state.gptConversations[idx] = {
            ...state.gptConversations[idx],
            ...state.activeGptConversation,
          };
        }
      }
    });
    builder.addCase(resetChat.fulfilled, (state) => {
      state.activeGptConversation = null;
      state.gptConversations = [];
      state.gptConversationsPagination = { ...initialPagination };
    });
    builder.addCase(updateGptConversationName.fulfilled, (state, { payload }) => {
      const updatedGptConversationArray = [...state.gptConversations].map((conversation) => {
        if (Number(conversation.id) === Number(payload?.id)) {
          return payload;
        }
        return conversation;
      });
      state.gptConversations = updatedGptConversationArray;
    });
    builder.addCase(deleteGptConversation.fulfilled, (state, { payload }) => {
      const updatedGptConversationArray = [...state.gptConversations].filter((conversation) => {
        if (Number(conversation.id) === Number(payload?.id)) {
          return false;
        }
        return true;
      });
      state.gptConversations = updatedGptConversationArray;
      state.gptConversationsPagination.total = Math.max(
        0,
        state.gptConversationsPagination.total - 1,
      );
      if (Number(state.activeGptConversation?.id) === Number(payload?.id)) {
        state.activeGptConversation = null;
      }
    });
    builder.addCase(fetchAndUpdateSpecificConversation.fulfilled, (state, { payload }) => {
      // Strapi's default findOne doesn't derive lastGenerated{Project,ProjectGroup}Id
      // — only findAllWithNestedStuff does, via SQL subqueries. Preserve the IDs
      // that generateFromPlanThunk.fulfilled / fetchGptConversations.fulfilled
      // wrote so the success-card "Voir le module" CTA survives a re-fetch
      // (CreateProjectFromAiGraph triggers one right after generation completes).
      const preserveIds = (existing, incoming) => ({
        ...incoming,
        lastGeneratedProjectId:
          incoming?.lastGeneratedProjectId ?? existing?.lastGeneratedProjectId,
        lastGeneratedProjectGroupId:
          incoming?.lastGeneratedProjectGroupId ?? existing?.lastGeneratedProjectGroupId,
      });

      const updatedGptConversationArray = [...state.gptConversations].map((conversation) => {
        if (Number(conversation.id) === Number(payload.id)) {
          return preserveIds(conversation, payload);
        }
        return conversation;
      });

      state.gptConversations = updatedGptConversationArray;

      if (state.activeGptConversation?.id === payload.id) {
        state.activeGptConversation = preserveIds(state.activeGptConversation, payload);
      }
    });
    builder.addCase(getMedia.fulfilled, (state, { payload }) => {
      const updatedMedia = payload;
      state.scenarioHelperContextPdfs = state.scenarioHelperContextPdfs.map((pdf) => {
        return Number(pdf.id) === Number(updatedMedia.id) ? updatedMedia : pdf;
      });
      state.scenarioHelperContextImages = state.scenarioHelperContextImages.map((img) => {
        return Number(img.id) === Number(updatedMedia.id) ? updatedMedia : img;
      });
    });
    builder.addCase(startPlanModeChat.fulfilled, (state, { payload }) => {
      state.activeGptConversation = payload;
      state.gptConversations.push(payload);
      state.gptConversationsPagination.total += 1;
    });
    builder.addCase(continuePlanModeChat.fulfilled, (state, { payload }) => {
      if (state.activeGptConversation) {
        state.activeGptConversation.conversation_metadata.userPrompts.push(payload.userPrompt);
        state.activeGptConversation.conversation_data.output.push(payload.botResponse);
        const idx = state.gptConversations.findIndex(
          (c) => c.id === state.activeGptConversation?.id,
        );
        if (idx !== -1) {
          state.gptConversations[idx] = {
            ...state.gptConversations[idx],
            ...state.activeGptConversation,
          };
        }
      }
    });
    builder.addCase(transitionToReady.fulfilled, (state, { payload }) => {
      if (
        state.activeGptConversation &&
        Number(state.activeGptConversation.id) === Number(payload.conversationId)
      ) {
        state.activeGptConversation.plan_phase = "ready_to_generate";
        state.activeGptConversation.conversation_metadata.plan = payload.plan;
        // Insert the plan as an inline conversation message.
        state.activeGptConversation.conversation_metadata.userPrompts.push("");
        state.activeGptConversation.conversation_data.output.push("__PLAN__");
        // Mirror the server-pinned boundary so loadPreviousConversation can
        // collapse intake history without a re-fetch round-trip.
        if (typeof payload.intakeBoundary === "number") {
          state.activeGptConversation.conversation_metadata.intake_boundary =
            payload.intakeBoundary;
        }
        const idx = state.gptConversations.findIndex(
          (c) => Number(c.id) === Number(payload.conversationId),
        );
        if (idx !== -1) {
          state.gptConversations[idx] = {
            ...state.gptConversations[idx],
            plan_phase: "ready_to_generate",
            conversation_metadata: {
              ...state.gptConversations[idx].conversation_metadata,
              plan: payload.plan,
              ...(typeof payload.intakeBoundary === "number" && {
                intake_boundary: payload.intakeBoundary,
              }),
            },
            conversation_data: {
              ...state.gptConversations[idx].conversation_data,
              output: [
                ...(state.gptConversations[idx].conversation_data?.output || []),
                "__PLAN__",
              ],
            },
          };
        }
      }
    });
    builder.addCase(refinePlanThunk.fulfilled, (state, { payload }) => {
      if (
        state.activeGptConversation &&
        Number(state.activeGptConversation.id) === Number(payload.conversationId)
      ) {
        state.activeGptConversation.conversation_metadata.plan = payload.plan;
        state.activeGptConversation.conversation_metadata.userPrompts.push(payload.message);
        state.activeGptConversation.conversation_data.output.push("__PLAN__");
        const idx = state.gptConversations.findIndex(
          (c) => Number(c.id) === Number(payload.conversationId),
        );
        if (idx !== -1) {
          state.gptConversations[idx] = {
            ...state.gptConversations[idx],
            conversation_metadata: {
              ...state.gptConversations[idx].conversation_metadata,
              plan: payload.plan,
              userPrompts: [
                ...(state.gptConversations[idx].conversation_metadata.userPrompts || []),
                payload.message,
              ],
            },
            conversation_data: {
              ...state.gptConversations[idx].conversation_data,
              output: [
                ...(state.gptConversations[idx].conversation_data?.output || []),
                "__PLAN__",
              ],
            },
          };
        }
      }
    });
    builder.addCase(generateFromPlanThunk.pending, (state, action) => {
      if (
        state.activeGptConversation &&
        Number(state.activeGptConversation.id) === Number(action.meta.arg.conversationId)
      ) {
        state.activeGptConversation.plan_phase = "generating";
        state.generationStartedAt = Date.now();
      }
    });
    builder.addCase(generateFromPlanThunk.fulfilled, (state, { payload }) => {
      if (
        state.activeGptConversation &&
        Number(state.activeGptConversation.id) === Number(payload.conversationId)
      ) {
        state.activeGptConversation.plan_phase = payload.planPhase;
        state.generationStartedAt = null;
        // Stash the freshly-minted ids on the conversation so the success
        // card in the chat-window can render an "Ouvrir le projet" link
        // without a re-fetch.
        if (payload.projectId !== null) {
          state.activeGptConversation.lastGeneratedProjectId = payload.projectId;
        }
        if (payload.projectGroupId !== null) {
          state.activeGptConversation.lastGeneratedProjectGroupId = payload.projectGroupId;
        }
        const idx = state.gptConversations.findIndex(
          (c) => Number(c.id) === Number(payload.conversationId),
        );
        if (idx !== -1) {
          state.gptConversations[idx] = {
            ...state.gptConversations[idx],
            plan_phase: payload.planPhase,
          };
        }
      }
    });
    builder.addCase(generateFromPlanThunk.rejected, (state) => {
      if (state.activeGptConversation && state.activeGptConversation.plan_phase === "generating") {
        state.activeGptConversation.plan_phase = "ready_to_generate";
      }
      state.generationStartedAt = null;
    });
    builder.addCase(syncUserWithServer.fulfilled, (state, { payload }) => {
      const incoming = payload.active_gpt_conversation;

      // Guard against stale sync responses clobbering plan-mode state.
      // Plan-mode thunks (transitionToReady, refinePlanThunk, generateFromPlanThunk)
      // are the authoritative source for plan_phase, plan, and conversation
      // history. A slow syncUserWithServer response must not overwrite them.
      // Build a new object (payload is frozen by Immer).
      const sameConversation =
        state.activeGptConversation?.id != null &&
        incoming?.id != null &&
        Number(state.activeGptConversation.id) === Number(incoming.id);

      const localPhase = state.activeGptConversation?.plan_phase;
      const serverPhase = incoming?.plan_phase;

      const merged =
        sameConversation && localPhase
          ? {
              ...incoming,
              plan_phase: adoptServerPhaseIfExitingGeneration(localPhase, serverPhase),
              // Keep the success-card "Voir le module" CTA stable across syncs.
              // The thunk's freshly-minted IDs win over a stale incoming sync;
              // when local is empty (e.g. first sync after a page reload), the
              // server's derived IDs hydrate the state.
              lastGeneratedProjectId:
                state.activeGptConversation.lastGeneratedProjectId ??
                incoming?.lastGeneratedProjectId,
              lastGeneratedProjectGroupId:
                state.activeGptConversation.lastGeneratedProjectGroupId ??
                incoming?.lastGeneratedProjectGroupId,
              conversation_metadata: {
                ...incoming.conversation_metadata,
                plan: state.activeGptConversation.conversation_metadata?.plan,
                userPrompts: state.activeGptConversation.conversation_metadata?.userPrompts,
                intake_boundary: state.activeGptConversation.conversation_metadata?.intake_boundary,
              },
              conversation_data: {
                ...incoming.conversation_data,
                output: state.activeGptConversation.conversation_data?.output,
              },
            }
          : incoming;

      state.activeGptConversation = merged;

      // Partition source_media_with_context into PDFs vs images.
      const allMedia: IMedia[] = merged?.source_media_with_context ?? [];
      const isImage = (m: IMedia) => (m?.type || "").toLowerCase().startsWith("image");
      state.scenarioHelperContextPdfs = allMedia.filter((m) => !isImage(m));
      state.scenarioHelperContextImages = allMedia.filter((m) => isImage(m));

      // Keep the conversations list in sync
      if (merged?.id) {
        const idx = state.gptConversations.findIndex((c) => c.id === merged.id);
        if (idx !== -1) {
          state.gptConversations[idx] = merged;
        }
      }
    });
  },
});

export const chatbotReducer = chatbotSlice.reducer;

export const getActiveGptConversation = (state: RootState) => state.chatbot.activeGptConversation;
export const getIdOfLatestProjectGroupCursedByAi = (state: RootState) =>
  state.chatbot.idOfLatestProjectGroupCursedByAi;
export const getScenarioHelperContextPdfs = (state: RootState) =>
  state.chatbot.scenarioHelperContextPdfs;
export const getScenarioHelperContextImages = (state: RootState) =>
  state.chatbot.scenarioHelperContextImages;
export const getPlanModeProgress = (state: RootState) => state.chatbot.planModeProgress;
export const getGenerationStartedAt = (state: RootState) => state.chatbot.generationStartedAt;
export const getScenarioHelperRagAgentIds = (state: RootState) =>
  state.chatbot.scenarioHelperRagAgentIds;
export const getChosenLlmProvider = (state: RootState) => state.chatbot.chosenLlmProvider;
export const getLastAiModuleContext = (state: RootState) => ({
  conversationId: state.chatbot.lastAiModuleConversationId,
  outputIndex: state.chatbot.lastAiModuleOutputIndex,
  generateBlockadeImages: state.chatbot.lastAiModuleGenerateBlockadeImages,
});

export const getGptConversationsPagination = (state: RootState) =>
  state.chatbot.gptConversationsPagination;

export const getGptConversationsSortedByRecency = (state: RootState) => {
  const sortedConversations = [...state.chatbot.gptConversations].sort((a, b) => {
    const timestampA = new Date(a.updatedAt).getTime();
    const timestampB = new Date(b.updatedAt).getTime();
    return timestampB - timestampA;
  });

  return sortedConversations;
};

export const {
  setActiveGptConversation,
  resetActiveGptConversation,
  setIdOfLatestProjectGroupCursedByAi,
  addScenarioHelperContextPdf,
  removeScenarioHelperContextPdf,
  setScenarioHelperContextPdf,
  resetScenarioHelperContextPdfs,
  addScenarioHelperContextImage,
  removeScenarioHelperContextImage,
  setScenarioHelperContextImages,
  resetScenarioHelperContextImages,
  setScenarioHelperRagAgentIds,
  resetScenarioHelperRagAgentIds,
  setChosenLlmProvider,
  setLastAiModuleContext,
  resetLastAiModuleContext,
  setPlanModeProgress,
  resetPlanModeProgress,
  mergeSceneGenerationMetadata,
} = chatbotSlice.actions;
